import React, { useState } from 'react';
import { rapprochementAssisteService } from '../../api/services';
import { RapprochementAssiste as ResultData } from '../../types';
import { MoneyDisplay } from '../../components/common/MoneyDisplay';
import { formatDate } from '../../utils/formatters';
import toast from 'react-hot-toast';

/**
 * Rapprochement bancaire assisté (TF-006) : coller/charger un relevé CSV
 * (date ; libellé ; montant) et apparier automatiquement aux paiements enregistrés.
 */
export function RapprochementAssiste() {
  const [csv, setCsv] = useState('');
  const [result, setResult] = useState<ResultData | null>(null);
  const [loading, setLoading] = useState(false);

  const onFile = async (f?: File) => {
    if (!f) return;
    setCsv(await f.text());
  };

  const run = async () => {
    if (!csv.trim()) { toast.error('Collez ou chargez un relevé CSV'); return; }
    setLoading(true);
    try {
      setResult(await rapprochementAssisteService.rapprocher(csv));
    } catch {
      toast.error('Rapprochement impossible');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="page-title">Rapprochement bancaire assisté</h1>
        <p className="page-subtitle">Importez un relevé CSV (date ; libellé ; montant) — appariement automatique aux paiements</p>
      </div>

      <div className="card space-y-3">
        <textarea
          className="input-field font-mono text-xs h-40"
          placeholder={'2026-06-05;Encaissement client;119.000\n2026-06-06;Virement fournisseur;-450.000'}
          value={csv}
          onChange={(e) => setCsv(e.target.value)}
        />
        <div className="flex items-center gap-3">
          <input type="file" accept=".csv,text/csv,text/plain"
                 onChange={(e) => onFile(e.target.files?.[0])} className="text-sm" />
          <button onClick={run} className="btn-primary" disabled={loading}>
            {loading ? 'Analyse…' : 'Rapprocher'}
          </button>
        </div>
      </div>

      {result && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="card"><p className="text-sm text-gray-500">Lignes rapprochées</p>
              <p className="text-2xl font-bold text-green-700">{result.nbRapproches}</p></div>
            <div className="card"><p className="text-sm text-gray-500">Non rapprochées</p>
              <p className="text-2xl font-bold text-amber-600">{result.nbNonRapproches}</p></div>
            <div className="card"><p className="text-sm text-gray-500">Total relevé</p>
              <MoneyDisplay amount={result.totalReleve} className="text-2xl font-bold" /></div>
          </div>

          <div className="card overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50"><tr className="text-left text-xs font-medium text-gray-500 uppercase">
                <th className="px-4 py-2">Date</th>
                <th className="px-4 py-2">Libellé</th>
                <th className="px-4 py-2 text-right">Montant</th>
                <th className="px-4 py-2">Statut</th>
                <th className="px-4 py-2">Paiement</th>
              </tr></thead>
              <tbody className="divide-y divide-gray-100">
                {result.lignes.map((l, i) => (
                  <tr key={i} className={l.rapproche ? '' : 'bg-amber-50'}>
                    <td className="px-4 py-2">{l.date ? formatDate(l.date) : '—'}</td>
                    <td className="px-4 py-2">{l.libelle || '—'}</td>
                    <td className="px-4 py-2 text-right"><MoneyDisplay amount={l.montant} size="sm" /></td>
                    <td className="px-4 py-2">
                      {l.rapproche
                        ? <span className="text-green-700">✓ rapprochée</span>
                        : <span className="text-amber-600">à pointer</span>}
                    </td>
                    <td className="px-4 py-2 text-gray-500">
                      {l.rapproche ? `${l.paiementReference || '#' + l.paiementId}${l.paiementDate ? ' · ' + formatDate(l.paiementDate) : ''}` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

export default RapprochementAssiste;
